import { useAuthentication, useMfaSetup, useQueryParam } from '@psyomics/ui-common';
import { CodeCaptureForm, PageLayout, PhoneCaptureForm } from '@psyomics/ui-components';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PATIENT_REDIRECT_WHITELIST } from '../../../app/redirect-whitelists';
import { useGetServiceUserQuery } from '../../account/hooks/use-get-service-user-query';

const MfaPhoneStep = ({
  onSubmit,
  navigateToCode,
}: {
  readonly onSubmit: (phone: string) => Promise<void>;
  readonly navigateToCode?: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <PageLayout title={t('page.mfaSetup.phone.title')}>
      <PhoneCaptureForm
        autoComplete="tel"
        buttonLabel={t('page.mfaSetup.phone.submit')}
        description={t('page.mfaSetup.phone.description')}
        errorInvalid={t('auth.common.errors.phoneNumberInvalid')}
        errorRequired={t('auth.common.errors.mobileNumberRequired')}
        inputLabel={t('page.mfaSetup.phone.label')}
        navigateToCode={navigateToCode}
        onSubmit={onSubmit}
        placeholder={t('page.mfaSetup.phone.placeholder')}
      />
    </PageLayout>
  );
};

const MfaCodeStep = ({
  phoneNumber,
  onSubmit,
  onResend,
  onChangeNumber,
  onStartAgain,
  isSelfReferral = false,
}: {
  readonly phoneNumber: string | null;
  readonly onSubmit: (code: string) => Promise<void>;
  readonly onResend: () => void | Promise<void>;
  readonly onChangeNumber: () => void;
  readonly onStartAgain: () => void;
  readonly isSelfReferral?: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <PageLayout title={t('page.mfaSetup.code.title')}>
      <CodeCaptureForm
        autoComplete="one-time-code"
        buttonLabel={t('page.mfaSetup.code.submit')}
        description={
          phoneNumber ? t('page.mfaSetup.code.description', { phoneNumber }) : t('auth.common.mfa.codeDescription')
        }
        errorRequired={t('auth.common.errors.confirmationCodeMissing')}
        inputLabel={
          isSelfReferral ? t('auth.register.mfa.selfReferralCodeInputLabel') : t('auth.register.mfa.codeInputLabel')
        }
        inputMode="numeric"
        onRetry={onResend}
        onSubmit={onSubmit}
        placeholder={t('page.mfaSetup.code.placeholder')}
        {...(isSelfReferral ? { onStartAgain } : { onChangeNumber })}
      />
    </PageLayout>
  );
};

const MfaSetup = () => {
  const redirect = useQueryParam('redirect');
  const { logout } = useAuthentication();
  const [serviceUser] = useGetServiceUserQuery();
  const mfaSetup = useMfaSetup({ redirect, redirectWhitelist: PATIENT_REDIRECT_WHITELIST });

  const selfReferralCode = useMemo(() => {
    if (!redirect) return undefined;
    const code = redirect.match(/\/self-referral\/(?:init\/)?([^/?#]+)/)?.[1];
    return code;
  }, [redirect]);

  return mfaSetup.showCode ? (
    <MfaCodeStep
      isSelfReferral={!serviceUser?.hasTrustReferral}
      onChangeNumber={mfaSetup.handleChangeNumber}
      onResend={mfaSetup.handleResend}
      onStartAgain={() => logout('self-referral/redirect', { ...(selfReferralCode ? { selfReferralCode } : {}) })}
      onSubmit={mfaSetup.handleCodeSubmit}
      phoneNumber={mfaSetup.phoneNumber}
    />
  ) : (
    <MfaPhoneStep navigateToCode={mfaSetup.navigateToCode} onSubmit={mfaSetup.handlePhoneSubmit} />
  );
};

export { MfaCodeStep, MfaSetup };
